import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import productionService from '../../../services/productionService';
import expenditureService from '../../../services/expenditureService';
import financeService from '../../../services/financeService';
import animalService from '../../../services/animalService';
import animalPurchasesService from '../../../services/animalPurchasesService';
import animalSalesService from '../../../services/animalSaleService';
import customerService from '../../../services/customerService';

const REPORT_TYPES = [
  { id: 'production', label: 'Production Report', icon: '🥛', color: '#4a90e2' },
  { id: 'expenditure', label: 'Expenditure Report', icon: '💸', color: '#e74c3c' },
  { id: 'finance', label: 'Finance Report', icon: '💰', color: '#27ae60' },
  { id: 'animals', label: 'Animals Report', icon: '🐄', color: '#f39c12' },
  { id: 'trading', label: 'Buy/Sell Report', icon: '🔄', color: '#8e44ad' },
  { id: 'customer_bill', label: 'Customer Bill', icon: '🧾', color: '#16a085' },
  { id: 'sale', label: 'Sale Report', icon: '📊', color: '#2980b9' },
  { id: 'pregnancy', label: 'Pregnancy Report', icon: '🐄', color: '#c0392b' },
];

// Opening freezer stock measured by owner (June 2). The Sale Report's running
// stock balance accumulates from here. One-line edit if ever re-measured.
const LEFTOVER_BASELINE = {
  date: '2026-06-02',
  cow: 90,
  buffalo: 37,
};

// Format a Date as YYYY-MM-DD using LOCAL components (avoids the UTC shift
// that toISOString() introduces — e.g. 1 Jun midnight PKT becoming 31 May UTC).
const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const today = toLocalYMD(new Date());
const monthStart = toLocalYMD(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatCurrency = (amount) => {
  return `Rs ${parseFloat(amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 0 })}`;
};

const ReportsTab = () => {
  const [selectedReport, setSelectedReport] = useState('production');
  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [animalsList, setAnimalsList] = useState([]);
  const [selectedAnimalTag, setSelectedAnimalTag] = useState('');

  // Load customers for the Customer Bill dropdown
  React.useEffect(() => {
    customerService.getAllCustomers()
      .then(res => setCustomers(res.data || []))
      .catch(err => console.error('Failed to load customers:', err));
  }, []);

  // Load animals for the Production Report dropdown
  React.useEffect(() => {
    animalService.getAllAnimals()
      .then(res => setAnimalsList(res.data || []))
      .catch(err => console.error('Failed to load animals:', err));
  }, []);

 const addPDFHeader = (doc, reportTitle, startDate, endDate) => {
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header background
    doc.setFillColor(44, 62, 80);
    doc.rect(0, 0, pageWidth, 50, 'F');

    // Farm name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Ahmad Dairy and Agriculture Farms', pageWidth / 2, 15, { align: 'center' });

    // Farm address + mobile
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('200 RB Lathianwala  |  Mobile: 0300-7671404', pageWidth / 2, 22, { align: 'center' });

    // Report title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(reportTitle, pageWidth / 2, 32, { align: 'center' });

    // Date range
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${formatDate(startDate)} — ${formatDate(endDate)}`, pageWidth / 2, 40, { align: 'center' });

    // Generated on
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.text(`Generated on: ${new Date().toLocaleString('en-PK')}`, pageWidth - 14, 56, { align: 'right' });

    // Divider
    doc.setDrawColor(44, 62, 80);
    doc.setLineWidth(0.5);
    doc.line(14, 58, pageWidth - 14, 58);

    return 62; // return Y position after header
  };

  const addSummaryCards = (doc, cards, startY) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const cardWidth = (pageWidth - 28 - (cards.length - 1) * 6) / cards.length;
    let x = 14;

    cards.forEach((card) => {
      // Card background
      doc.setFillColor(...card.bgColor);
      doc.roundedRect(x, startY, cardWidth, 22, 2, 2, 'F');

      // Card label
      doc.setTextColor(...card.textColor);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(card.label.toUpperCase(), x + cardWidth / 2, startY + 8, { align: 'center' });

      // Card value
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(card.value, x + cardWidth / 2, startY + 17, { align: 'center' });

      x += cardWidth + 6;
    });

    return startY + 28;
  };

  // ─── PRODUCTION REPORT ───────────────────────────────────────────
  const generateProductionReport = async () => {
    // Fetch production records and the animals list (for name lookup by tag)
    const [prodRes, animalsRes] = await Promise.all([
      productionService.getAllProduction(),
      animalService.getAllAnimals(),
    ]);
    const allData = prodRes.data || [];
    const allAnimals = animalsRes.data || [];

    // Build a tag -> name lookup from the animals list
    const nameByTag = {};
    allAnimals.forEach(a => {
      if (a.tag_number != null) nameByTag[String(a.tag_number)] = a.name || '-';
    });

    // Filter production records to the selected date range (and animal, if chosen)
    const filtered = allData.filter(p => {
      const d = p.date?.split('T')[0];
      const inRange = d >= startDate && d <= endDate;
      if (!inRange) return false;
      if (selectedAnimalTag) {
        const tag = String(p.animal_tag || p.tag_number || '');
        return tag === String(selectedAnimalTag);
      }
      return true;
    });

    // Merge morning + evening into one row per (date + animal tag)
    const mergedMap = {};
    filtered.forEach(p => {
      const d = p.date?.split('T')[0] || '';
      const tag = String(p.animal_tag || p.tag_number || '-');
      const key = `${d}__${tag}`;
      if (!mergedMap[key]) {
        // Prefer a name on the record; otherwise look it up by tag
        const name = p.animal_name || nameByTag[tag] || '-';
        mergedMap[key] = { date: d, tag, name, morning: 0, evening: 0 };
      }
      mergedMap[key].morning += parseFloat(p.morning_production || 0);
      mergedMap[key].evening += parseFloat(p.evening_production || 0);
    });

    // Sort by date, then by tag number ascending
    const rows = Object.values(mergedMap).sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      const ta = parseInt(a.tag, 10);
      const tb = parseInt(b.tag, 10);
      if (!isNaN(ta) && !isNaN(tb)) return ta - tb;
      return a.tag < b.tag ? -1 : 1;
    });

    const totalMorning = rows.reduce((s, r) => s + r.morning, 0);
    const totalEvening = rows.reduce((s, r) => s + r.evening, 0);
    const totalMilk = totalMorning + totalEvening;

    const doc = new jsPDF();
    let y = addPDFHeader(doc, 'MILK PRODUCTION REPORT', startDate, endDate);

    // Summary cards
    y = addSummaryCards(doc, [
      { label: 'Total Records', value: `${rows.length}`, bgColor: [232, 245, 253], textColor: [21, 101, 192] },
      { label: 'Morning Milk', value: `${totalMorning.toFixed(1)} L`, bgColor: [232, 245, 253], textColor: [21, 101, 192] },
      { label: 'Evening Milk', value: `${totalEvening.toFixed(1)} L`, bgColor: [232, 245, 253], textColor: [21, 101, 192] },
      { label: 'Total Milk', value: `${totalMilk.toFixed(1)} L`, bgColor: [27, 94, 32], textColor: [255, 255, 255] },
    ], y);

    // Ensure a clean, known-good font state before rendering the table
    // (prevents intermittent character garbling in text cells).
    doc.setFont('helvetica', 'normal');

    // Table — one row per animal per day; equal centered columns
    autoTable(doc, {
      startY: y,
      head: [['#', 'Date', 'Animal Tag', 'Animal Name', 'Morning (L)', 'Evening (L)', 'Total (L)']],
      body: rows.map((r, i) => [
        String(i + 1),
        String(formatDate(r.date)),
        String(r.tag),
        String(r.name),
        r.morning.toFixed(1),
        r.evening.toFixed(1),
        (r.morning + r.evening).toFixed(1),
      ]),
      styles: { font: 'helvetica', fontStyle: 'normal', fontSize: 9, cellPadding: 3, halign: 'center', valign: 'middle' },
      headStyles: { font: 'helvetica', fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold', halign: 'center' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 'auto' },
        5: { cellWidth: 'auto' },
        6: { cellWidth: 'auto' },
      },
      foot: [['', '', '', 'TOTAL', totalMorning.toFixed(1), totalEvening.toFixed(1), totalMilk.toFixed(1)]],
      footStyles: { font: 'helvetica', fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold', halign: 'center' },
    });

    doc.save(`Production_Report_${startDate}_to_${endDate}.pdf`);
  };

  // ─── EXPENDITURE REPORT ──────────────────────────────────────────
  const generateExpenditureReport = async () => {
    const response = await expenditureService.getAllExpenditures({ from_date: startDate, to_date: endDate });
    const allData = response.data || [];

    const totalAmount = allData.reduce((s, e) => s + parseFloat(e.amount || 0), 0);

    // Group by category
    const byCategory = {};
    allData.forEach(e => {
      const cat = e.category || 'Other';
      byCategory[cat] = (byCategory[cat] || 0) + parseFloat(e.amount || 0);
    });

    const doc = new jsPDF();
    let y = addPDFHeader(doc, 'EXPENDITURE REPORT', startDate, endDate);

    // Summary cards
    y = addSummaryCards(doc, [
      { label: 'Total Records', value: `${allData.length}`, bgColor: [255, 235, 238], textColor: [198, 40, 40] },
      { label: 'Total Expenditure', value: formatCurrency(totalAmount), bgColor: [198, 40, 40], textColor: [255, 255, 255] },
      { label: 'Categories', value: `${Object.keys(byCategory).length}`, bgColor: [255, 235, 238], textColor: [198, 40, 40] },
    ], y);

    // Category breakdown
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Expenditure by Category', 14, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['Category', 'Amount']],
      body: Object.entries(byCategory).map(([cat, amt]) => [cat, formatCurrency(amt)]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [198, 40, 40], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });

    y = doc.lastAutoTable.finalY + 8;

    // Detailed table
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Detailed Expenditure Records', 14, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Date', 'Category', 'Description', 'Amount']],
      body: allData.map((e, i) => [
        i + 1,
        formatDate(e.expense_date || e.date),
        e.category || '-',
        e.description || '-',
        formatCurrency(e.amount),
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: { 4: { halign: 'right' } },
      foot: [['', '', '', 'TOTAL', formatCurrency(totalAmount)]],
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
    });

    doc.save(`Expenditure_Report_${startDate}_to_${endDate}.pdf`);
  };

  // ─── FINANCE REPORT ──────────────────────────────────────────────
  const generateFinanceReport = async () => {
    const [salesRes, expendRes] = await Promise.all([
      financeService.getAllMilkSales(),
      expenditureService.getAllExpenditures({ from_date: startDate, to_date: endDate }),
    ]);

    const allSales = (salesRes.data || []).filter(s => {
      const d = s.sale_date?.split('T')[0];
      return d >= startDate && d <= endDate;
    });
    const allExp = expendRes.data || [];

    const totalRevenue = allSales.reduce((s, sale) => s + parseFloat(sale.total_amount || 0), 0);
    const totalExpenditure = allExp.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const netProfit = totalRevenue - totalExpenditure;

    const doc = new jsPDF();
    let y = addPDFHeader(doc, 'FINANCE REPORT', startDate, endDate);

    // Summary cards
    y = addSummaryCards(doc, [
      { label: 'Total Revenue', value: formatCurrency(totalRevenue), bgColor: [232, 245, 233], textColor: [27, 94, 32] },
      { label: 'Total Expenditure', value: formatCurrency(totalExpenditure), bgColor: [255, 235, 238], textColor: [198, 40, 40] },
      { label: 'Net Profit', value: formatCurrency(netProfit), bgColor: netProfit >= 0 ? [27, 94, 32] : [198, 40, 40], textColor: [255, 255, 255] },
    ], y);

    // Sales table
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Revenue — Milk Sales', 14, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Date', 'Customer', 'Quantity (L)', 'Rate/L', 'Total Amount', 'Payment']],
      body: allSales.map((s, i) => [
        i + 1,
        formatDate(s.sale_date),
        s.customer_name || 'Walk-in',
        parseFloat(s.quantity || 0).toFixed(1),
        formatCurrency(s.price_per_liter),
        formatCurrency(s.total_amount),
        s.payment_type || '-',
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [27, 94, 32], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      foot: [['', '', '', '', 'TOTAL', formatCurrency(totalRevenue), '']],
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
    });

    y = doc.lastAutoTable.finalY + 8;

    // Expenditure table
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Expenditure', 14, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Date', 'Category', 'Description', 'Amount']],
      body: allExp.map((e, i) => [
        i + 1,
        formatDate(e.expense_date || e.date),
        e.category || '-',
        e.description || '-',
        formatCurrency(e.amount),
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [198, 40, 40], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: { 4: { halign: 'right' } },
      foot: [['', '', '', 'TOTAL', formatCurrency(totalExpenditure)]],
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
    });

    y = doc.lastAutoTable.finalY + 8;

    // Net profit summary
    doc.setFillColor(44, 62, 80);
    doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`NET PROFIT: ${formatCurrency(netProfit)}`, doc.internal.pageSize.getWidth() / 2, y + 10, { align: 'center' });

    doc.save(`Finance_Report_${startDate}_to_${endDate}.pdf`);
  };

  // ─── ANIMALS REPORT ──────────────────────────────────────────────
  const generateAnimalsReport = async () => {
    const response = await animalService.getAllAnimals();
    const allAnimals = response.data || [];

    const active = allAnimals.filter(a => a.is_active !== false);
    const inactive = allAnimals.filter(a => a.is_active === false);
    const milking = allAnimals.filter(a => a.is_active !== false && a.animal_type?.toLowerCase() !== 'dry');
    const dry = allAnimals.filter(a => a.is_active !== false && a.animal_type?.toLowerCase() === 'dry');

    const doc = new jsPDF('landscape');
    let y = addPDFHeader(doc, 'ANIMALS REPORT', startDate, endDate);

    // Summary cards
    y = addSummaryCards(doc, [
      { label: 'Total Animals', value: `${allAnimals.length}`, bgColor: [255, 243, 224], textColor: [230, 81, 0] },
      { label: 'Active', value: `${active.length}`, bgColor: [232, 245, 233], textColor: [27, 94, 32] },
      { label: 'Inactive', value: `${inactive.length}`, bgColor: [255, 235, 238], textColor: [198, 40, 40] },
      { label: 'Milking', value: `${milking.length}`, bgColor: [232, 244, 253], textColor: [21, 101, 192] },
      { label: 'Dry', value: `${dry.length}`, bgColor: [243, 229, 245], textColor: [106, 27, 154] },
    ], y);

    // Full animals table
    autoTable(doc, {
      startY: y,
      head: [['#', 'Tag No.', 'Name', 'Type', 'Breed', 'Gender', 'DOB', 'Age', 'Weight (kg)', 'Milk Type', 'Purchase Date', 'Purchase Price', 'Status', 'Notes']],
      body: allAnimals.map((a, i) => {
        const dob = a.date_of_birth ? new Date(a.date_of_birth) : null;
        const age = dob ? `${Math.floor((new Date() - dob) / (365.25 * 24 * 60 * 60 * 1000))} yrs` : '-';
        return [
          i + 1,
          a.tag_number || '-',
          a.name || '-',
          a.animal_type || '-',
          a.breed || '-',
          a.gender || '-',
          formatDate(a.date_of_birth),
          age,
          a.weight || '-',
          a.milk_type || '-',
          formatDate(a.purchase_date),
          a.purchase_price ? formatCurrency(a.purchase_price) : '-',
          a.is_active !== false ? 'Active' : 'Inactive',
          a.notes || '-',
        ];
      }),
      styles: { fontSize: 7.5, cellPadding: 2.5 },
      headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      didParseCell: (data) => {
        if (data.column.index === 12 && data.section === 'body') {
          if (data.cell.raw === 'Active') {
            data.cell.styles.textColor = [27, 94, 32];
            data.cell.styles.fontStyle = 'bold';
          } else {
            data.cell.styles.textColor = [198, 40, 40];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
    });

    doc.save(`Animals_Report_${today}.pdf`);
  };

  // ─── BUY/SELL ANIMALS REPORT ─────────────────────────────────────
  const generateTradingReport = async () => {
    const [purchasesRes, salesRes, animalsRes] = await Promise.all([
      animalPurchasesService.getAllPurchases(),
      animalSalesService.getAllSales(),
      animalService.getAllAnimals(),
    ]);

    const purchases = purchasesRes.data || [];
    const sales = salesRes.data || [];
    const currentAnimals = (animalsRes.data || []).filter(a => a.is_active !== false);

    // Current animals asset value — match with purchase records
    const totalSalePurchaseCost = sales.reduce((s, s2) => s + parseFloat(s2.purchase_price || 0), 0);
    const totalSaleRevenue = sales.reduce((s, s2) => s + parseFloat(s2.sale_price || 0), 0);
    const totalProfitLoss = sales.reduce((s, s2) => s + parseFloat(s2.profit_loss || 0), 0);
    const currentAssetValue = purchases
      .filter(p => currentAnimals.find(a => a.tag_number === p.tag_number))
      .reduce((s, p) => s + parseFloat(p.purchase_price || 0), 0);

    const doc = new jsPDF();
    let y = addPDFHeader(doc, 'ANIMAL BUY/SELL REPORT', startDate, endDate);

    // Summary cards
    y = addSummaryCards(doc, [
      { label: 'Current Animals', value: `${currentAnimals.length}`, bgColor: [255, 243, 224], textColor: [230, 81, 0] },
      { label: 'Asset Value', value: formatCurrency(currentAssetValue), bgColor: [232, 245, 233], textColor: [27, 94, 32] },
      { label: 'Total Sold', value: `${sales.length}`, bgColor: [243, 229, 245], textColor: [106, 27, 154] },
      { label: 'Profit/Loss', value: formatCurrency(totalProfitLoss), bgColor: totalProfitLoss >= 0 ? [27, 94, 32] : [198, 40, 40], textColor: [255, 255, 255] },
    ], y);

    // Current Animals section
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Current Animals — Farm Assets', 14, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Tag No.', 'Name', 'Type', 'Breed', 'Purchase Date', 'Purchase Price', 'Seller']],
      body: purchases
        .filter(p => currentAnimals.find(a => a.tag_number === p.tag_number))
        .map((p, i) => {
          const animal = currentAnimals.find(a => a.tag_number === p.tag_number);
          return [
            i + 1,
            p.tag_number || '-',
            animal?.name || '-',
            p.animal_type || '-',
            p.breed || '-',
            formatDate(p.purchase_date),
            formatCurrency(p.purchase_price),
            p.seller_name || '-',
          ];
        }),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [27, 94, 32], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: { 6: { halign: 'right' } },
      foot: [['', '', '', '', '', 'TOTAL ASSET VALUE', formatCurrency(currentAssetValue), '']],
      footStyles: { fillColor: [27, 94, 32], textColor: 255, fontStyle: 'bold' },
    });

    y = doc.lastAutoTable.finalY + 10;

    // Sold Animals section
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    doc.text('Sold Animals — Trading History', 14, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Tag No.', 'Name', 'Type', 'Sale Date', 'Buy Price', 'Sale Price', 'Profit/Loss', 'Buyer']],
      body: sales.map((s, i) => [
        i + 1,
        s.tag_number || '-',
        s.animal_name || '-',
        s.animal_type || '-',
        formatDate(s.sale_date),
        formatCurrency(s.purchase_price),
        formatCurrency(s.sale_price),
        formatCurrency(s.profit_loss),
        s.buyer_name || '-',
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [142, 68, 173], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: { 6: { halign: 'right' }, 7: { halign: 'right' } },
      didParseCell: (data) => {
        if (data.column.index === 7 && data.section === 'body') {
          const val = parseFloat(sales[data.row.index]?.profit_loss || 0);
          data.cell.styles.textColor = val >= 0 ? [27, 94, 32] : [198, 40, 40];
          data.cell.styles.fontStyle = 'bold';
        }
      },
      foot: [['', '', '', '', '', formatCurrency(totalSalePurchaseCost), formatCurrency(totalSaleRevenue), formatCurrency(totalProfitLoss), '']],
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
    });

    doc.save(`Animal_Trading_Report_${today}.pdf`);
  };

  // ─── CUSTOMER BILL REPORT ────────────────────────────────────────
  const generateCustomerBillReport = async () => {
    if (!selectedCustomerId) {
      alert('Please select a customer first');
      return;
    }

    const customer = customers.find(c => String(c.id) === String(selectedCustomerId));
    if (!customer) {
      alert('Selected customer not found');
      return;
    }

    const salesRes = await financeService.getAllMilkSales();
    const customerSales = (salesRes.data || []).filter(s => {
      if (String(s.customer_id) !== String(selectedCustomerId)) return false;
      const d = s.sale_date?.includes('T') ? s.sale_date.split('T')[0] : s.sale_date?.substring(0, 10);
      return d >= startDate && d <= endDate;
    });

    // Group by date + session
    const grouped = {};
    customerSales.forEach(s => {
      const d = s.sale_date?.includes('T') ? s.sale_date.split('T')[0] : s.sale_date?.substring(0, 10);
      const session = s.session || '-';
      const key = `${d}__${session}`;
      if (!grouped[key]) {
        grouped[key] = { date: d, session, cow: 0, buffalo: 0, amount: 0 };
      }
      const qty = parseFloat(s.quantity || 0);
      const amt = parseFloat(s.total_amount || 0);
      if (s.milk_type === 'Cow') grouped[key].cow += qty;
      else if (s.milk_type === 'Buffalo') grouped[key].buffalo += qty;
      grouped[key].amount += amt;
    });

    // Sort rows by date then session (Morning before Evening)
    const rows = Object.values(grouped).sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      return a.session === 'Morning' ? -1 : 1;
    });

    const totalCow = rows.reduce((s, r) => s + r.cow, 0);
    const totalBuffalo = rows.reduce((s, r) => s + r.buffalo, 0);
    const periodTotal = rows.reduce((s, r) => s + r.amount, 0);
    const outstanding = parseFloat(customer.outstanding_balance || 0);

    const doc = new jsPDF();
    let y = addPDFHeader(doc, 'CUSTOMER MILK BILL', startDate, endDate);

    // Customer info line
    doc.setTextColor(44, 62, 80);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Customer: ${customer.name}`, 14, y);
    if (customer.preferred_session) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Session: ${customer.preferred_session}`, doc.internal.pageSize.getWidth() - 14, y, { align: 'right' });
    }
    y += 8;

    // Summary cards
    y = addSummaryCards(doc, [
      { label: 'Total Cow', value: `${totalCow.toFixed(1)} L`, bgColor: [232, 245, 233], textColor: [27, 94, 32] },
      { label: 'Total Buffalo', value: `${totalBuffalo.toFixed(1)} L`, bgColor: [232, 244, 253], textColor: [21, 101, 192] },
      { label: 'Period Total', value: formatCurrency(periodTotal), bgColor: [22, 160, 133], textColor: [255, 255, 255] },
      { label: 'Outstanding', value: formatCurrency(outstanding), bgColor: outstanding > 0 ? [198, 40, 40] : [27, 94, 32], textColor: [255, 255, 255] },
    ], y);

    // Daily detail table
    autoTable(doc, {
      startY: y,
      head: [['#', 'Date', 'Session', 'Cow (L)', 'Buffalo (L)', 'Amount']],
      body: rows.map((r, i) => [
        i + 1,
        formatDate(r.date),
        r.session,
        r.cow > 0 ? r.cow.toFixed(1) : '-',
        r.buffalo > 0 ? r.buffalo.toFixed(1) : '-',
        formatCurrency(r.amount),
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [22, 160, 133], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: { 5: { halign: 'right' } },
      foot: [['', '', 'TOTAL', totalCow.toFixed(1), totalBuffalo.toFixed(1), formatCurrency(periodTotal)]],
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
    });

    y = doc.lastAutoTable.finalY + 8;

    // Period total + Outstanding highlighted box
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(22, 160, 133);
    doc.rect(14, y, pageWidth - 28, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`This Period: ${formatCurrency(periodTotal)}`, 18, y + 8);
    doc.text(`Total Outstanding: ${formatCurrency(outstanding)}`, pageWidth - 18, y + 8, { align: 'right' });
    y += 20;

    // Footer — farm contact info
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(14, y, pageWidth - 14, y);
    y += 6;
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Ahmad Dairy and Agriculture Farms', pageWidth / 2, y, { align: 'center' });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('200 RB Lathianwala  |  Mobile: 0300-7671404', pageWidth / 2, y, { align: 'center' });

    const safeName = (customer.name || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`Bill_${safeName}_${startDate}_to_${endDate}.pdf`);
  };

  // ─── SALE REPORT ─────────────────────────────────────────────────
  const generateSaleReport = async () => {
    // Fetch production and sales (same services the other reports use)
    const [prodRes, salesRes] = await Promise.all([
      productionService.getAllProduction(),
      financeService.getAllMilkSales(),
    ]);

    const allProd = prodRes.data || [];
    const allSales = salesRes.data || [];

    // Normalise a date value to YYYY-MM-DD
    const dkey = (v) => {
      if (!v) return '';
      return v.includes('T') ? v.split('T')[0] : v.substring(0, 10);
    };

    // Per-day production totals by type (from baseline date onward, so the
    // running stock balance is correct even if the user picks a later range)
    const prodByDay = {};
    allProd.forEach(p => {
      const d = dkey(p.date);
      if (!d || d < LEFTOVER_BASELINE.date) return;
      if (!prodByDay[d]) prodByDay[d] = { cow: 0, buffalo: 0 };
      const qty = parseFloat(p.morning_production || 0) + parseFloat(p.evening_production || 0);
      if (p.animal_type === 'Cow') prodByDay[d].cow += qty;
      else if (p.animal_type === 'Buffalo') prodByDay[d].buffalo += qty;
    });

    // Per-day sales totals by type (exclude Leftover-session sales)
    const salesByDay = {};
    allSales.forEach(s => {
      const d = dkey(s.sale_date);
      if (!d || d < LEFTOVER_BASELINE.date) return;
      if (s.session === 'Leftover') return;
      if (!salesByDay[d]) salesByDay[d] = { cow: 0, buffalo: 0 };
      const qty = parseFloat(s.quantity || 0);
      if (s.milk_type === 'Cow') salesByDay[d].cow += qty;
      else if (s.milk_type === 'Buffalo') salesByDay[d].buffalo += qty;
    });

    // Walk every day from baseline -> endDate, accumulate running stock.
    // Only push days inside [startDate, endDate] into the displayed rows.
    const rows = [];
    let runCow = LEFTOVER_BASELINE.cow;
    let runBuf = LEFTOVER_BASELINE.buffalo;

    const cursor = new Date(LEFTOVER_BASELINE.date + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');

    while (cursor <= end) {
      const d = toLocalYMD(cursor);
      const p = prodByDay[d] || { cow: 0, buffalo: 0 };
      const s = salesByDay[d] || { cow: 0, buffalo: 0 };

      runCow += p.cow - s.cow;
      runBuf += p.buffalo - s.buffalo;

      if (d >= startDate && d <= endDate) {
        rows.push({
          date: d,
          cowProd: p.cow,
          cowSold: s.cow,
          cowStock: runCow,
          bufProd: p.buffalo,
          bufSold: s.buffalo,
          bufStock: runBuf,
        });
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    // Period totals (over displayed rows)
    const tCowProd = rows.reduce((a, r) => a + r.cowProd, 0);
    const tCowSold = rows.reduce((a, r) => a + r.cowSold, 0);
    const tBufProd = rows.reduce((a, r) => a + r.bufProd, 0);
    const tBufSold = rows.reduce((a, r) => a + r.bufSold, 0);

    const doc = new jsPDF();
    let y = addPDFHeader(doc, 'DAILY SALE REPORT', startDate, endDate);

    // Summary cards — sold totals + current stock at end of period
    y = addSummaryCards(doc, [
      { label: 'Cow Sold', value: `${tCowSold.toFixed(1)} L`, bgColor: [232, 245, 233], textColor: [27, 94, 32] },
      { label: 'Buffalo Sold', value: `${tBufSold.toFixed(1)} L`, bgColor: [232, 244, 253], textColor: [21, 101, 192] },
      { label: 'Cow Stock', value: `${runCow.toFixed(1)} L`, bgColor: [27, 94, 32], textColor: [255, 255, 255] },
      { label: 'Buffalo Stock', value: `${runBuf.toFixed(1)} L`, bgColor: [21, 101, 192], textColor: [255, 255, 255] },
    ], y);

    // Daily detail table
    autoTable(doc, {
      startY: y,
      head: [[
        '#', 'Date',
        'Cow Prod', 'Cow Sold', 'Cow Stock',
        'Buf Prod', 'Buf Sold', 'Buf Stock',
      ]],
      body: rows.map((r, i) => [
        i + 1,
        formatDate(r.date),
        r.cowProd.toFixed(1),
        r.cowSold.toFixed(1),
        r.cowStock.toFixed(1),
        r.bufProd.toFixed(1),
        r.bufSold.toFixed(1),
        r.bufStock.toFixed(1),
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' },
        5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
      },
      foot: [[
        '', 'TOTAL',
        tCowProd.toFixed(1), tCowSold.toFixed(1), runCow.toFixed(1),
        tBufProd.toFixed(1), tBufSold.toFixed(1), runBuf.toFixed(1),
      ]],
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
    });

    doc.save(`Sale_Report_${startDate}_to_${endDate}.pdf`);
  };

  // ─── PREGNANCY STATUS REPORT ─────────────────────────────────────
  const generatePregnancyReport = async () => {
    const response = await animalService.getAllAnimals();
    const allAnimals = response.data || [];

    // Only animals marked pregnant (pregnancy_status === 'Yes')
    const pregnant = allAnimals.filter(
      a => String(a.pregnancy_status || '').toLowerCase() === 'yes'
    );

    // Today at local midnight, for whole-day difference calculations
    const nowLocal = new Date();
    const todayMid = new Date(nowLocal.getFullYear(), nowLocal.getMonth(), nowLocal.getDate());

    // Build rows with days-to-delivery and status
    const rows = pregnant.map(a => {
      const edd = a.expected_delivery_date
        ? new Date((a.expected_delivery_date.includes('T')
            ? a.expected_delivery_date.split('T')[0]
            : String(a.expected_delivery_date).substring(0, 10)) + 'T00:00:00')
        : null;

      let daysVal = null;     // +N = overdue by N, else N = days remaining
      let daysText = '-';
      let status = 'Unknown';

      if (edd) {
        const diffDays = Math.round((edd - todayMid) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
          // overdue — show +N
          const overdueBy = Math.abs(diffDays);
          daysVal = overdueBy;
          daysText = `+${overdueBy}`;
          status = 'Overdue';
        } else if (diffDays === 0) {
          daysVal = 0;
          daysText = 'Due today';
          status = 'Due Soon';
        } else {
          daysVal = diffDays;
          daysText = `${diffDays}`;
          status = diffDays <= 7 ? 'Due Soon' : (diffDays <= 30 ? 'Upcoming' : 'On Track');
        }
      }

      return {
        tag: a.tag_number || '-',
        name: a.name || '-',
        type: a.type || '-',
        breed: a.breed || '-',
        pregnancyDate: a.pregnancy_date || null,
        edd: a.expected_delivery_date || null,
        eddSort: edd ? edd.getTime() : Number.POSITIVE_INFINITY,
        daysText,
        status,
      };
    });

    // Sort by expected delivery date, soonest first (overdue at top)
    rows.sort((a, b) => a.eddSort - b.eddSort);

    // Summary counts
    const totalPregnant = rows.length;
    const overdueCount  = rows.filter(r => r.status === 'Overdue').length;
    const dueSoonCount   = rows.filter(r => r.status === 'Due Soon' || r.status === 'Upcoming').length;
    const onTrackCount   = rows.filter(r => r.status === 'On Track').length;

    const doc = new jsPDF();
    let y = addPDFHeader(doc, 'PREGNANCY STATUS REPORT', today, today);

    // Summary cards
    y = addSummaryCards(doc, [
      { label: 'Total Pregnant', value: `${totalPregnant}`, bgColor: [232, 244, 253], textColor: [21, 101, 192] },
      { label: 'Overdue', value: `${overdueCount}`, bgColor: overdueCount > 0 ? [198, 40, 40] : [232, 245, 233], textColor: overdueCount > 0 ? [255, 255, 255] : [27, 94, 32] },
      { label: 'Due <= 30 Days', value: `${dueSoonCount}`, bgColor: [255, 243, 224], textColor: [230, 81, 0] },
      { label: 'On Track', value: `${onTrackCount}`, bgColor: [232, 245, 233], textColor: [27, 94, 32] },
    ], y);

    // Table
    autoTable(doc, {
      startY: y,
      head: [['#', 'Tag', 'Name', 'Type', 'Breed', 'Pregnancy Date', 'Expected Delivery', 'Days', 'Status']],
      body: rows.map((r, i) => [
        i + 1,
        r.tag,
        r.name,
        r.type,
        r.breed,
        formatDate(r.pregnancyDate),
        formatDate(r.edd),
        r.daysText,
        r.status,
      ]),
      styles: { fontSize: 9, cellPadding: 3, halign: 'center', valign: 'middle' },
      headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold', halign: 'center' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      columnStyles: {
        2: { halign: 'left' },
        4: { halign: 'left' },
      },
      didParseCell: (data) => {
        // Color the Status cell by urgency
        if (data.section === 'body' && data.column.index === 8) {
          const st = rows[data.row.index]?.status;
          if (st === 'Overdue')      { data.cell.styles.textColor = [198, 40, 40];  data.cell.styles.fontStyle = 'bold'; }
          else if (st === 'Due Soon'){ data.cell.styles.textColor = [230, 81, 0];   data.cell.styles.fontStyle = 'bold'; }
          else if (st === 'Upcoming'){ data.cell.styles.textColor = [230, 81, 0]; }
          else if (st === 'On Track'){ data.cell.styles.textColor = [27, 94, 32]; }
        }
        // Bold the days cell for overdue rows
        if (data.section === 'body' && data.column.index === 7) {
          const st = rows[data.row.index]?.status;
          if (st === 'Overdue') { data.cell.styles.textColor = [198, 40, 40]; data.cell.styles.fontStyle = 'bold'; }
        }
      },
    });

    doc.save(`Pregnancy_Report_${today}.pdf`);
  };

  const handleGenerateReport = async () => {
    if (selectedReport !== 'animals' && selectedReport !== 'trading' && selectedReport !== 'pregnancy') {
      if (!startDate || !endDate) {
        alert('Please select both start and end dates');
        return;
      }
      if (startDate > endDate) {
        alert('Start date cannot be after end date');
        return;
      }
    }

    setLoading(true);
    try {
      switch (selectedReport) {
        case 'production': await generateProductionReport(); break;
        case 'expenditure': await generateExpenditureReport(); break;
        case 'finance': await generateFinanceReport(); break;
        case 'animals': await generateAnimalsReport(); break;
        case 'trading': await generateTradingReport(); break;
        case 'customer_bill': await generateCustomerBillReport(); break;
        case 'sale': await generateSaleReport(); break;
        case 'pregnancy': await generatePregnancyReport(); break;
        default: break;
      }
    } catch (error) {
      console.error('Report generation error:', error);
      alert('❌ Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', color: '#2c3e50', fontWeight: '700', margin: '0 0 8px 0' }}>
          📋 Reports
        </h2>
        <p style={{ color: '#7f8c8d', margin: 0 }}>
          Generate and download professional PDF reports
        </p>
      </div>

      {/* Report Type Selection */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '16px', marginTop: 0 }}>
          1. Select Report Type
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
          {REPORT_TYPES.map(report => (
            <div
              key={report.id}
              onClick={() => setSelectedReport(report.id)}
              style={{
                padding: '16px',
                borderRadius: '10px',
                border: `2px solid ${selectedReport === report.id ? report.color : '#e0e0e0'}`,
                background: selectedReport === report.id ? `${report.color}15` : 'white',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>{report.icon}</div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: selectedReport === report.id ? report.color : '#2c3e50' }}>
                {report.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Customer selector - only for Customer Bill */}
      {selectedReport === 'production' && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '16px', marginTop: 0 }}>
            Select Animal
          </h3>
          <select
            value={selectedAnimalTag}
            onChange={(e) => setSelectedAnimalTag(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
          >
            <option value="">All Animals</option>
            {animalsList
              .slice()
              .sort((a, b) => String(a.tag_number).localeCompare(String(b.tag_number), undefined, { numeric: true }))
              .map(a => (
                <option key={a.id || a.tag_number} value={a.tag_number}>
                  {a.tag_number} - {a.name || '-'}
                </option>
              ))}
          </select>
        </div>
      )}

     

      

      

      

      

      {selectedReport === 'customer_bill' && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '16px', marginTop: 0 }}>
            Select Customer
          </h3>
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
          >
            <option value="">— Choose a customer —</option>
            {customers
              .slice()
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.preferred_session ? `(${c.preferred_session})` : ''}
                </option>
              ))}
          </select>
        </div>
      )}

      {/* Date Range - hide for animals & trading reports */}
      {selectedReport !== 'animals' && selectedReport !== 'trading' && selectedReport !== 'pregnancy' && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '16px', marginTop: 0 }}>
            2. Select Date Range
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Quick date shortcuts */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
            {[
              { label: 'This Month', start: monthStart, end: today },
              { label: 'Last 7 Days', start: toLocalYMD(new Date(Date.now() - 7 * 86400000)), end: today },
              { label: 'Last 30 Days', start: toLocalYMD(new Date(Date.now() - 30 * 86400000)), end: today },
              { label: 'This Year', start: `${new Date().getFullYear()}-01-01`, end: today },
            ].map(shortcut => (
              <button
                key={shortcut.label}
                onClick={() => { setStartDate(shortcut.start); setEndDate(shortcut.end); }}
                style={{
                  padding: '6px 14px',
                  background: '#f0f4f8',
                  border: '1px solid #d0d7de',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#2c3e50',
                  cursor: 'pointer',
                }}
              >
                {shortcut.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Trading report note */}
      {selectedReport === 'trading' && (
        <div style={{ background: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#6a1b9a' }}>
            🔄 This report shows current farm animal assets with their purchase values, and complete trading history with profit/loss on sold animals.
          </p>
        </div>
      )}

      {/* Animals report note */}
      {selectedReport === 'animals' && (
        <div style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#e65100' }}>
            🐄 The Animals Report shows all animals with complete details including tag numbers, breed, age, weight, milk type, purchase info, and current status.
          </p>
        </div>
      )}

      {/* Sale report note */}
      {selectedReport === 'sale' && (
        <div style={{ background: '#eaf2fb', border: '1px solid #aaccef', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#1f5a8c' }}>
            📊 The Sale Report shows day-by-day production and sales for cow and buffalo, plus the running stock balance (milk remaining) at the end of each day.
          </p>
        </div>
      )}

      {/* Pregnancy report note */}
      {selectedReport === 'pregnancy' && (
        <div style={{ background: '#fdecea', border: '1px solid #f5b7b1', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#a93226' }}>
            🐄 The Pregnancy Report lists all pregnant animals sorted by expected delivery date (soonest first), with days remaining and status. Overdue animals show as +days past the expected date.
          </p>
        </div>
      )}

      {/* Generate Button */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '16px', marginTop: 0 }}>
          {(selectedReport === 'animals' || selectedReport === 'trading' || selectedReport === 'pregnancy') ? '2.' : '3.'} Generate Report
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#7f8c8d' }}>
              Ready to generate: <strong style={{ color: '#2c3e50' }}>
                {REPORT_TYPES.find(r => r.id === selectedReport)?.label}
              </strong>
              {selectedReport !== 'animals' && selectedReport !== 'trading' && selectedReport !== 'pregnancy' && (
                <> for <strong style={{ color: '#2c3e50' }}>{formatDate(startDate)} — {formatDate(endDate)}</strong></>
              )}
            </p>
          </div>
          <button
            onClick={handleGenerateReport}
            disabled={loading}
            style={{
              padding: '14px 32px',
              background: loading ? '#95a5a6' : 'linear-gradient(135deg, #2c3e50 0%, #4a6741 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'all 0.2s',
            }}
          >
            {loading ? (
              <>⏳ Generating...</>
            ) : (
              <>📥 Download PDF</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportsTab;